import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ExternalLink, ListChecks } from "lucide-react";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { ActivityBlock } from "../components/issues/activity";
import { AttachmentsBlock } from "../components/issues/attachments";
import { FactoryCard } from "../components/issues/factory-card";
import { FactoryTab } from "../components/issues/factory-tab";
import { IssueEditor } from "../components/issues/issue-editor";
import { LifecycleSwitch } from "../components/issues/lifecycle-switch";
import { useIssuesMeta } from "../components/issues/meta";
import { PreviewEnvironment } from "../components/issues/preview-environment";
import { IssueLifecycle, IssueProperties, PropertiesSidebar } from "../components/issues/properties-sidebar";
import { RelationsBlock } from "../components/issues/relations";
import { SignOffCard } from "../components/issues/sign-off";
import { SubIssuesBlock } from "../components/issues/sub-issues";
import { TestStepsBlock } from "../components/issues/test-steps";
import { TestingInstructionsBlock } from "../components/issues/testing-instructions";
import { EmptyState, PageSkeleton } from "../components/page";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { useMediaQuery } from "../hooks/use-media-query";
import { useIssueLinkState } from "../lib/issue-modal";
import { useFactoryAccess, useIsManager, useIsMember } from "../lib/permissions";

const VIEWS = ["issue", "factory"] as const;

export function IssueDetailPage() {
  const { identifier = "" } = useParams();
  const queryClient = useQueryClient();
  const readOnly = !useIsManager();
  const canCreate = useIsMember();
  const factoryAccess = useFactoryAccess();
  const [view, setView] = useQueryState("view", parseAsStringLiteral(VIEWS).withDefault("issue"));
  const linkState = useIssueLinkState();
  // Matches the `xl:` grid below: under it the page is one column with no sidebar.
  const wide = useMediaQuery("(min-width: 80rem)");
  const metaQuery = useIssuesMeta();
  const meta = metaQuery.data;
  const query = useQuery({
    queryKey: ["issue", identifier],
    queryFn: () => api.issue(identifier),
    refetchInterval: 10_000,
  });
  const issue = query.data?.issue;

  useEffect(() => {
    if (!identifier) return;
    void api.markInboxSeen(identifier).then(
      () => queryClient.invalidateQueries({ queryKey: ["inbox"] }),
      () => undefined,
    );
  }, [identifier, queryClient]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["issue", identifier] });
    void queryClient.invalidateQueries({ queryKey: ["issues"] });
  };
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateIssue>[1]) => api.updateIssue(identifier, patch),
    onSuccess: refresh,
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Update failed"),
  });
  const setArchived = useMutation({
    mutationFn: (archived: boolean) => api.setIssueArchived(identifier, archived),
    onSuccess: (_issue, archived) => {
      toast.success(archived ? "Issue archived" : "Issue restored");
      refresh();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not update archive"),
  });

  if (query.isLoading) return <PageSkeleton />;
  if (!issue) {
    return (
      <EmptyState icon={ListChecks} title="Issue not found" description="It may have been deleted.">
        <Button variant="outline" asChild>
          <Link to="/issues">Back to issues</Link>
        </Button>
      </EmptyState>
    );
  }

  const taskId = factoryAccess.tab ? issue.taskId : null;
  const active = taskId ? view : "issue";
  const tabs = taskId ? (
    <LifecycleSwitch
      value={active}
      onChange={(next) => void setView(next === "issue" ? null : "factory")}
      issue={issue}
      meta={meta}
      taskId={taskId}
    />
  ) : null;

  // Body first, then a narrow properties column on wide screens. Sections in the body are
  // divided by their headings and spacing alone; only interactive surfaces draw a border.
  // The title, properties column and Factory card stay put; the tabs swap the body under the title.
  return (
    <div className="mx-auto grid w-full max-w-9xl items-start gap-6 xl:grid-cols-[minmax(0,1fr)_21rem] xl:gap-x-8">
      <div className="fx-rise flex min-w-0 flex-col gap-7">
        <Breadcrumb className="text-xs">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to={linkState?.backgroundLocation ?? "/issues"}
                  state={linkState?.backgroundLocation.state}
                >
                  Issues
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {issue.parent && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/issues/${issue.parent.identifier}`} state={linkState}>
                      {issue.parent.identifier}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-mono text-xs">{issue.identifier}</BreadcrumbPage>
            </BreadcrumbItem>
            {jiraLink(issue.externalRef) && (
              <BreadcrumbItem className="ml-2">
                <a
                  href={jiraLink(issue.externalRef)!.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-xs text-muted-foreground hover:text-foreground"
                  title="Imported from Jira"
                >
                  Jira {jiraLink(issue.externalRef)!.key}
                  <ExternalLink className="size-3" />
                </a>
              </BreadcrumbItem>
            )}
          </BreadcrumbList>
        </Breadcrumb>

        {issue.archivedAt && (
          <Alert>
            <Archive />
            <AlertTitle>This issue is archived</AlertTitle>
            <AlertDescription>
              {readOnly
                ? "It stays out of the default lists."
                : issue.subIssues.total > 0
                  ? "It and its sub-issues stay out of the default lists until you restore it."
                  : "It stays out of the default lists until you restore it."}
            </AlertDescription>
            {!readOnly && (
              <AlertAction>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={setArchived.isPending}
                  onClick={() => setArchived.mutate(false)}
                >
                  Restore
                </Button>
              </AlertAction>
            )}
          </Alert>
        )}

        <div className="flex min-w-0 flex-col gap-7">
          <IssueEditor
            key={issue.id}
            issue={issue}
            pending={update.isPending}
            saved={update.isSuccess}
            onSave={(patch) => update.mutate(patch)}
            readOnly={readOnly}
            hideDescription={active === "factory"}
            belowTitle={
              (!wide || tabs) && (
                <>
                  {!wide && (
                    <IssueProperties
                      collapsible
                      issue={issue}
                      meta={meta}
                      update={update.mutate}
                      onChanged={refresh}
                      readOnly={readOnly}
                    />
                  )}
                  {tabs}
                </>
              )
            }
          />
          {active === "factory" && taskId ? (
            <>
              {!wide && <FactoryCard issue={issue} onChanged={refresh} />}
              <FactoryTab taskId={taskId} />
            </>
          ) : (
            <>
              {!wide && <SignOffCard issue={issue} onChanged={refresh} />}
              {!wide &&
                (readOnly ? (
                  <PreviewEnvironment issue={issue} onChanged={refresh} />
                ) : (
                  <div className="grid items-start gap-4 md:grid-cols-2">
                    <FactoryCard issue={issue} onChanged={refresh} />
                    <PreviewEnvironment issue={issue} onChanged={refresh} />
                  </div>
                ))}
              {issue.testSteps.length ? (
                <TestStepsBlock issue={issue} onChanged={refresh} />
              ) : (
                <TestingInstructionsBlock
                  key={`testing-${issue.id}`}
                  issue={issue}
                  pending={update.isPending}
                  saved={update.isSuccess}
                  onSave={(patch) => update.mutate(patch)}
                  readOnly={readOnly}
                />
              )}
              <AttachmentsBlock issue={issue} onChanged={refresh} readOnly={readOnly} />
              <SubIssuesBlock issue={issue} meta={meta} readOnly={!canCreate} />
              <RelationsBlock issue={issue} onChanged={refresh} readOnly={readOnly} />
              <ActivityBlock issue={issue} />
              {!wide && (
                <IssueLifecycle
                  issue={issue}
                  onArchive={(archived) => setArchived.mutate(archived)}
                  archivePending={setArchived.isPending}
                  readOnly={readOnly}
                />
              )}
            </>
          )}
        </div>
      </div>
      {wide && (
        <PropertiesSidebar
          issue={issue}
          meta={meta}
          update={update.mutate}
          onChanged={refresh}
          onArchive={(archived) => setArchived.mutate(archived)}
          archivePending={setArchived.isPending}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

/** The Jira issue this one was imported from, when there is one. */
function jiraLink(externalRef: Record<string, unknown> | null): { key: string; url: string } | null {
  if (externalRef?.source !== "jira") return null;
  const key = externalRef.jiraKey;
  const url = externalRef.url;
  return typeof key === "string" && typeof url === "string" ? { key, url } : null;
}
